/**
 * useTagOrders / useTagOrder
 *
 * Hooks for the tag-shop order history endpoints:
 *   GET /shop/tags/orders        → list (company-scoped server-side)
 *   GET /shop/tags/orders/:id    → single order detail
 *
 * Tracking is sourced from Shopify's NATIVE fulfillment data (decision 2026-06-26:
 * no AfterShip): trackingNumber, trackingUrl, trackingCompany (carrier),
 * shipmentStatus (Shopify fulfillment displayStatus), and estimatedDeliveryAt.
 * AfterShip stays an optional future enrichment (a polled checkpoint timeline)
 * only if native fulfillment events prove insufficient.
 */
import { useQuery, useMutation } from '@tanstack/react-query'
import type { ExampleKind } from '@repo/types'
import { createApiCall } from 'services'
import { TAG_SHOP_MOCK_ENABLED, mockTagOrders, getMockTagOrder } from 'mocks/tagShopMock'

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface TagShopCustomizationPlacement {
  xPct: number
  yPct: number
  scalePct: number
  rotation: number
}

export interface TagShopOrderCustomization {
  frontLogoUrl?: string
  backLogoUrl?: string
  color?: string
  finish?: string
  logoFinish?: string
  frontPlacement?: TagShopCustomizationPlacement | null
  backPlacement?: TagShopCustomizationPlacement | null
}

export interface TagShopOrderLineItem {
  id: string
  title: string
  quantity: number
  /** Product/variant image URL for this line (empty string when none). */
  image: string
  /** Custom design applied to this line (logos, color/finish), if any. */
  customization?: TagShopOrderCustomization | null
  /** Numeric Shopify product id. Used to build the cart productId composite
   *  key: "<productId>#<variantId>". Empty string when the API did not return
   *  a product GID (older orders or stripped response). */
  productId: string
  variant: {
    id: string
    sku: string
    price: string
    title: string
  }
}

export interface TagShopOrderItem {
  id: string
  name: string
  createdAt: string
  totalPriceSet: {
    shopMoney: {
      amount: string
      currencyCode: string
    }
  }
  fulfillmentStatus: string
  financialStatus: string
  customer: {
    firstName: string
    lastName: string
    email: string
  }
  shippingAddress: {
    address1: string
    city: string
    country: string
    zip: string
    label?: string
  }
  paymentMethod?: { brand: string; last4: string; funding?: string } | null
  orderedBy?: { name?: string; email?: string } | null
  lineItems: TagShopOrderLineItem[]
  tags: string[]
  note: string
  trackingNumber: string
  trackingUrl: string
  trackingCompany: string
  shipmentStatus: string
  estimatedDeliveryAt: string | null
  /**
   * VRG-512 — `orders.example_kind`: seeded sample data, not a real order. Drives the
   * `ExampleKindBadge` in the ID column and the `IntroDataBanner`'s `surfaceHasSamples`
   * condition on the brand-facing Tags -> Orders list.
   *
   * `null` on every Shopify-sourced row (an order in Shopify is real by construction)
   * and `'intro'` on a row served by the endpoint's company-scoped local fallback —
   * `ListTagShopOrdersUseCase.getIntroOrdersFallback`, which fires when the brand has no
   * Shopify customer id (the state every brand seeing the intro tour is in) or when its
   * customer-scoped Shopify fetch comes back empty. It never fires on a Shopify ERROR:
   * an error is not evidence the brand has no orders.
   *
   * A non-null value changes three things on this list: the `ExampleKindBadge` under the
   * id, the `IntroDataBanner`'s `surfaceHasSamples` condition, and — because the row has
   * no Shopify detail page and no Shopify variant — the suppression of the id link and
   * of both kebab actions. See `Orders.tsx` for that last part.
   */
  example_kind?: ExampleKind | null
}

export interface TagShopOrdersResponse {
  orders: TagShopOrderItem[]
  pageInfo: {
    hasNextPage: boolean
    endCursor: string
  }
}

type UseTagOrdersParams = {
  limit?: number
  cursor?: string
}

// ─── Defensive transform ───────────────────────────────────────────────────────

function transformOrder(raw: any): TagShopOrderItem {
  return {
    id: String(raw?.id ?? ''),
    name: String(raw?.name ?? ''),
    createdAt: String(raw?.createdAt ?? new Date().toISOString()),
    totalPriceSet: {
      shopMoney: {
        amount: String(raw?.totalPriceSet?.shopMoney?.amount ?? '0'),
        currencyCode: String(raw?.totalPriceSet?.shopMoney?.currencyCode ?? 'USD'),
      },
    },
    fulfillmentStatus: String(raw?.fulfillmentStatus ?? 'unfulfilled'),
    financialStatus: String(raw?.financialStatus ?? 'pending'),
    customer: {
      firstName: String(raw?.customer?.firstName ?? ''),
      lastName: String(raw?.customer?.lastName ?? ''),
      email: String(raw?.customer?.email ?? ''),
    },
    shippingAddress: {
      address1: String(raw?.shippingAddress?.address1 ?? ''),
      city: String(raw?.shippingAddress?.city ?? ''),
      country: String(raw?.shippingAddress?.country ?? ''),
      zip: String(raw?.shippingAddress?.zip ?? ''),
      label: raw?.shippingAddress?.label || undefined,
    },
    paymentMethod: raw?.paymentMethod ?? null,
    orderedBy: raw?.orderedBy ?? null,
    lineItems: (raw?.lineItems ?? []).map((item: any) => ({
      id: String(item?.id ?? ''),
      title: String(item?.title ?? ''),
      quantity: Number(item?.quantity ?? 0),
      image: String(item?.image ?? ''),
      customization: item?.customization ?? null,
      productId: String(item?.productId ?? ''),
      variant: {
        id: String(item?.variant?.id ?? ''),
        sku: String(item?.variant?.sku ?? ''),
        price: String(item?.variant?.price ?? '0'),
        title: String(item?.variant?.title ?? ''),
      },
    })),
    tags: Array.isArray(raw?.tags) ? raw.tags : [],
    note: String(raw?.note ?? ''),
    trackingNumber: String(raw?.trackingNumber ?? ''),
    trackingUrl: String(raw?.trackingUrl ?? ''),
    trackingCompany: String(raw?.trackingCompany ?? ''),
    shipmentStatus: String(raw?.shipmentStatus ?? ''),
    estimatedDeliveryAt: raw?.estimatedDeliveryAt ?? null,
    // VRG-512. Deliberately NOT coerced with `String()` like its neighbours: this field
    // is a discriminated union whose null is load-bearing (`ExampleKindBadge` renders
    // nothing for a falsy kind, which is how every real row stays unannotated), and
    // `String(undefined)` would hand it the truthy string "undefined" and badge every
    // order in the table. `?? null` normalises absent and null to the one value the
    // badge and the banner's `surfaceHasSamples` both test against.
    example_kind: raw?.example_kind ?? null,
  }
}

// ─── Query keys ────────────────────────────────────────────────────────────────

export const tagOrdersKeys = {
  all: ['tag-shop-orders'] as const,
  lists: () => [...tagOrdersKeys.all, 'list'] as const,
  list: (params: UseTagOrdersParams) => [...tagOrdersKeys.lists(), params] as const,
  details: () => [...tagOrdersKeys.all, 'detail'] as const,
  detail: (id: string) => [...tagOrdersKeys.details(), id] as const,
}

// ─── useTagOrders ─────────────────────────────────────────────────────────────

export function useTagOrders(params: UseTagOrdersParams = {}) {
  const stableParams = {
    limit: params.limit ?? 50,
    ...(params.cursor ? { cursor: params.cursor } : {}),
  }

  return useQuery({
    queryKey: tagOrdersKeys.list(stableParams),
    queryFn: async (): Promise<TagShopOrdersResponse> => {
      // DEV-only mock (VITE_TAG_SHOP_MOCK).
      if (TAG_SHOP_MOCK_ENABLED) {
        return { orders: mockTagOrders, pageInfo: { hasNextPage: false, endCursor: '' } }
      }

      const response = await createApiCall({
        method: 'GET',
        url: '/shop/tags/orders',
        params: stableParams,
      })

      const raw = response.data as any
      return {
        orders: (raw?.orders ?? []).map(transformOrder),
        pageInfo: {
          hasNextPage: Boolean(raw?.pageInfo?.hasNextPage ?? false),
          endCursor: String(raw?.pageInfo?.endCursor ?? ''),
        },
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
  })
}

// ─── useTagOrderResolution (post-checkout) ────────────────────────────────────

export interface TagOrderResolution {
  /** pending — webhook hasn't completed the order yet; ready — orderId is set. */
  status: 'pending' | 'ready' | 'failed'
  orderId: string | null
}

/**
 * Polls the resolve-by-reconciliation endpoint so the confirmation screen can
 * deep-link to the specific order once the payment_intent.succeeded webhook has
 * completed the draft into a real Shopify order (usually within ~1-2s). Stops
 * polling once the status is no longer 'pending'.
 */
export function useTagOrderResolution(reconciliationId: string | undefined) {
  return useQuery({
    queryKey: [...tagOrdersKeys.all, 'resolution', reconciliationId ?? ''] as const,
    queryFn: async (): Promise<TagOrderResolution> => {
      const response = await createApiCall({
        method: 'GET',
        url: `/shop/tags/orders/by-reconciliation/${reconciliationId}`,
      })
      const raw = response.data as any
      return {
        status: (raw?.status ?? 'pending') as TagOrderResolution['status'],
        orderId: raw?.orderId ?? null,
      }
    },
    enabled: Boolean(reconciliationId),
    refetchInterval: query => {
      const data = query.state.data as TagOrderResolution | undefined
      return data && data.status !== 'pending' ? false : 1500
    },
    staleTime: 0,
    retry: 1,
  })
}

// ─── useTagOrder (single) ─────────────────────────────────────────────────────

export function useTagOrder(id: string | undefined) {
  return useQuery({
    queryKey: tagOrdersKeys.detail(id ?? ''),
    queryFn: async (): Promise<TagShopOrderItem | null> => {
      if (!id) return null
      // DEV-only mock (VITE_TAG_SHOP_MOCK).
      if (TAG_SHOP_MOCK_ENABLED) {
        return getMockTagOrder(id)
      }
      const response = await createApiCall({
        method: 'GET',
        url: `/shop/tags/orders/${id}`,
      })
      return transformOrder(response.data)
    },
    enabled: Boolean(id),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  })
}

// ─── useTagOrderDocumentDownload ──────────────────────────────────────────────

/**
 * VERA-891: download the Vera-branded order document (PDF) for one of the
 * authenticated company's own orders.
 *
 * Hits the COMPANY-scoped `/shop/tags/orders/:id/document`, not the internal
 * `/shop/orders/:id/document` — the latter is Vera-staff only and would 403
 * here. Fetched as a blob because the route is session-gated, so a bare
 * <a href> would carry no auth header.
 */
export function useTagOrderDocumentDownload() {
  return useMutation({
    mutationFn: async ({ orderId, orderName }: { orderId: string; orderName?: string }) => {
      const response = await createApiCall({
        method: 'GET',
        // MUST be encoded: `order.id` is a full GID
        // ("gid://shopify/Order/123"), whose slashes would otherwise split the
        // path and 404 the route.
        url: `/shop/tags/orders/${encodeURIComponent(orderId)}/document`,
        responseType: 'blob',
      })

      // The api client does not reject non-2xx, so an error response arrives
      // here as a JSON blob. Without this guard it would be saved as a .pdf —
      // a corrupt file and no error shown.
      const contentType = String(response.headers?.['content-type'] ?? '')
      if (response.status >= 400 || !contentType.includes('application/pdf')) {
        throw new Error(`Order document request failed (${response.status})`)
      }

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Order-${(orderName ?? '').replace(/[^a-zA-Z0-9-_]/g, '') || 'document'}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    },
  })
}
